/**
 * Per-timeframe indicator settings.
 *
 * Standard MACD/RSI periods are calibrated for daily bars and lag badly on
 * 5-minute ones, so each timeframe gets its own defaults. On top of that a user
 * can customize an indicator per timeframe — tuning MACD on the 1D chart
 * doesn't disturb the 1Y chart. Settings are shared across tickers.
 *
 * Resolution order:  INDICATOR_DEFAULTS → timeframe override → user's custom
 */
import { INDICATOR_DEFAULTS } from './indicatorDefaults';

export const INTRADAY_TIMEFRAMES = ['1d', '1w'];
export const DAILY_TIMEFRAMES = ['1m', '3m', '6m', '1y', '2y', '5y'];
export const ALL_TIMEFRAMES = [...INTRADAY_TIMEFRAMES, ...DAILY_TIMEFRAMES];

export const SETTINGS_VERSION = 2;

/**
 * Sparse overrides — only the keys that differ from INDICATOR_DEFAULTS.
 * Shorter bars move faster, so the averages shorten with them.
 */
export const TIMEFRAME_INDICATOR_DEFAULTS = {
  '1d': {   // 5-minute bars
    macd: { fast: 6, slow: 13, signal: 5 },
    rsi: { period: 9 },
  },
  '1w': {   // 30-minute bars
    macd: { fast: 8, slow: 17, signal: 9 },
    rsi: { period: 12 },
  },
};

const emptyStore = () => ({ __v: SETTINGS_VERSION, byTimeframe: {}, lastCustom: {} });

/** Merge that respects the two color conventions: `colors` object on panel
 *  indicators, scalar `color` on overlays. */
function mergeSettings(base, override) {
  if (!override) return { ...base };
  const merged = { ...base, ...override };
  if (base?.colors || override?.colors) {
    merged.colors = { ...(base?.colors || {}), ...(override?.colors || {}) };
  }
  return merged;
}

/** Defaults for an indicator on a timeframe, before any user customization. */
export function getTimeframeDefaults(indicator, timeframe) {
  const base = INDICATOR_DEFAULTS[indicator] || {};
  const override = TIMEFRAME_INDICATOR_DEFAULTS[timeframe]?.[indicator];
  return mergeSettings(base, override);
}

/** Fully resolved settings, including the user's custom values if any. */
export function getEffectiveSettings(indicator, timeframe, byTimeframe = {}) {
  const defaults = getTimeframeDefaults(indicator, timeframe);
  return mergeSettings(defaults, byTimeframe?.[timeframe]?.[indicator]);
}

/**
 * Has the user actually changed this indicator on this timeframe?
 *
 * A value comparison, not a key-presence check: the settings modal always emits
 * a complete object, so opening and saving without edits would otherwise mark
 * the indicator custom forever.
 */
export function isCustomized(indicator, timeframe, byTimeframe = {}) {
  const stored = byTimeframe?.[timeframe]?.[indicator];
  if (!stored || typeof stored !== 'object') return false;

  const defaults = getTimeframeDefaults(indicator, timeframe);
  return Object.keys(stored).some((key) => {
    if (key === 'enabled') return false;
    if (key === 'colors') {
      const a = stored.colors || {};
      const b = defaults.colors || {};
      return Object.keys(a).some(c => a[c] !== b[c]);
    }
    return stored[key] !== defaults[key];
  });
}

/** How many of the active indicators are customized on this timeframe. */
export function countCustomized(indicators = [], timeframe, byTimeframe = {}) {
  return indicators.filter(ind => isCustomized(ind, timeframe, byTimeframe)).length;
}

/** Is there a saved custom value to restore after a reset? */
export function hasLastCustom(indicator, timeframe, lastCustom = {}) {
  return !!lastCustom?.[timeframe]?.[indicator];
}

/**
 * Bring stored settings up to the current shape.
 *
 * v1 was a flat { [indicator]: settings } map that predates timeframes. Those
 * values were tuned on daily charts, so they're copied to every daily timeframe
 * and the new intraday ones start on their own defaults.
 */
export function migrateSettings(stored) {
  if (!stored || typeof stored !== 'object') return emptyStore();

  if (stored.__v === SETTINGS_VERSION) {
    return {
      __v: SETTINGS_VERSION,
      byTimeframe: stored.byTimeframe && typeof stored.byTimeframe === 'object' ? stored.byTimeframe : {},
      lastCustom: stored.lastCustom && typeof stored.lastCustom === 'object' ? stored.lastCustom : {},
    };
  }

  const flat = Object.entries(stored).filter(
    ([key, value]) => key !== '__v' && value && typeof value === 'object'
  );
  if (flat.length === 0) return emptyStore();

  const byTimeframe = {};
  DAILY_TIMEFRAMES.forEach((tf) => {
    byTimeframe[tf] = Object.fromEntries(flat.map(([k, v]) => [k, { ...v }]));
  });
  return {
    __v: SETTINGS_VERSION,
    byTimeframe,
    lastCustom: JSON.parse(JSON.stringify(byTimeframe)),
  };
}

/** Write a custom setting, recording it as the timeframe's last custom value. */
export function setCustomSettings(store, timeframe, indicator, settings) {
  const next = migrateSettings(store);
  next.byTimeframe = {
    ...next.byTimeframe,
    [timeframe]: { ...(next.byTimeframe[timeframe] || {}), [indicator]: settings },
  };
  next.lastCustom = {
    ...next.lastCustom,
    [timeframe]: { ...(next.lastCustom[timeframe] || {}), [indicator]: settings },
  };
  return next;
}

/** Drop the override, keeping lastCustom so it can be restored. */
export function resetIndicator(store, timeframe, indicator) {
  const next = migrateSettings(store);
  const forTimeframe = { ...(next.byTimeframe[timeframe] || {}) };
  delete forTimeframe[indicator];
  next.byTimeframe = { ...next.byTimeframe, [timeframe]: forTimeframe };
  return next;
}

/** Reset every listed indicator on this timeframe. */
export function resetAll(store, timeframe, indicators = []) {
  return indicators.reduce((acc, ind) => resetIndicator(acc, timeframe, ind), store);
}

/** Put back the most recently saved custom value for this indicator. */
export function restoreIndicator(store, timeframe, indicator) {
  const next = migrateSettings(store);
  const saved = next.lastCustom?.[timeframe]?.[indicator];
  if (!saved) return next;
  next.byTimeframe = {
    ...next.byTimeframe,
    [timeframe]: { ...(next.byTimeframe[timeframe] || {}), [indicator]: saved },
  };
  return next;
}

/** Restore every listed indicator that has a saved custom value. */
export function restoreAll(store, timeframe, indicators = []) {
  return indicators.reduce((acc, ind) => restoreIndicator(acc, timeframe, ind), store);
}

/**
 * Build the indicator_config payload for the chart API.
 *
 * Returns null when everything resolves to the plain, non-timeframe defaults —
 * which keeps daily requests byte-identical to what they were before per-
 * timeframe settings existed (and avoids a needless refetch).
 */
export function buildTimeframeIndicatorConfig(indicators = [], timeframe, byTimeframe = {}) {
  if (!indicators.length) return null;

  const config = {};
  let differsFromBase = false;

  indicators.forEach((ind) => {
    const effective = getEffectiveSettings(ind, timeframe, byTimeframe);
    config[ind] = effective;
    const base = INDICATOR_DEFAULTS[ind] || {};
    if (Object.keys(effective).some(k => k !== 'colors' && k !== 'enabled' && effective[k] !== base[k])) {
      differsFromBase = true;
    }
  });

  return differsFromBase ? config : null;
}
